import { Paper, Tab, Tabs } from '@material-ui/core'

import * as R from 'ramda'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'

import TabPanel from '../components/common/TabPanel'
import Loading from '../components/loading'
import config from '../config'
import { COMPANY_DETAIL_PAGE_TYPE } from '../constants/enums/common.enum.constant'
import {
  COMPANY_STATUS,
  COMPANY_STATUS_TRANSITION_TYPE,
  PROFILE_CATEGORY
} from '../constants/enums/company.enum.constant'
import { COUNTRY_LIST } from '../constants/enums/country.enum.constant'
import CompanyForm from '../containers/company/CompanyForm'
import CompanyProductTabPanel from '../containers/company/CompanyProductTabPanel'
import DetailTitleBar from '../containers/detailTitleBar'
import HorizontalGridDivider from '../containers/horizontalGridDivider'
import SideMenu from '../containers/sideMenu'
import StyledMain from '../containers/styledMain'
import TopBar from '../containers/topBar'
import VerticalGridDivder from '../containers/verticalGridDivider'
import { Pagination } from '../interfaces/common'
import {
  CompanyDetailInterface,
  CompanyFormInterface
} from '../interfaces/company'
import {
  ProductFormInterface,
  ProductListInterface
} from '../interfaces/product'
import {
  createCompanyRequest,
  getCompanyDetailRequest,
  updateCompanyRequest,
  updateCompanyStatusRequest
} from '../redux/company/actions'
import {
  getCompanyDetail,
  getIsCreateOrUpdatingCompany,
  getIsFetchingCompanyDetail
} from '../redux/company/selectors'
import {
  archiveProductRequest,
  createProductRequest,
  getCompanyProductListRequest,
  updateProductRequest
} from '../redux/product/actions'
import { getProductList } from '../redux/product/selectors'
import {
  createAndUpdateCompanyFormToApiConvertor,
  createProductFromToApiConvertor,
  updateProductFromToApiConvertor
} from '../utils/service'

const emptyCompanyDetail: CompanyDetailInterface = {
  companyId: '',
  companyNo: '',
  sequentialNo: 0,
  name: '',
  profileCategory: PROFILE_CATEGORY.buyer,
  country: COUNTRY_LIST['Hong Kong SAR'],
  contactEmail: '',
  status: COMPANY_STATUS.new,
  exportCountries: [],
  importCountries: [],
  galleryFiles: [],
  certificateFiles: [],
  verified: false
}

const CompanyDetail = ({
  match: {
    params: { companyId: id }
  }
}: {
  match: {
    params: {
      companyId: string
    }
  }
  [key: string]: any
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { pathname } = useLocation()

  const checkPageType = (
    type: keyof typeof COMPANY_DETAIL_PAGE_TYPE
  ): ((path: string) => boolean) =>
    R.compose(R.equals(type) as any, R.head, R.reverse as any, R.split('/'))
  const disableForm = checkPageType(COMPANY_DETAIL_PAGE_TYPE.view)(pathname)
  const newForm = checkPageType(COMPANY_DETAIL_PAGE_TYPE.create)(pathname)

  const companyDetail = newForm
    ? emptyCompanyDetail
    : (useSelector(getCompanyDetail) as CompanyDetailInterface)
  if (companyDetail?.status === COMPANY_STATUS.archived && !disableForm) {
    history.push(`/company/${companyDetail.companyId}/view`)
  }

  if (companyDetail?.status !== COMPANY_STATUS.archived && disableForm) {
    history.push(`/company/${companyDetail.companyId}/edit`)
  }

  const isFetchingCompanyDetail = useSelector(getIsFetchingCompanyDetail)
  const isCreateOrUpdatingCompany = useSelector(getIsCreateOrUpdatingCompany)

  const paginatedProductList = useSelector(getProductList) as Pagination<
    ProductListInterface
  >
  const companyProductList = paginatedProductList?.result ?? []

  const formHandlers = {
    createCompany: (createdCompany: CompanyFormInterface) => {
      dispatch(
        createCompanyRequest(
          createAndUpdateCompanyFormToApiConvertor(createdCompany)
        )
      )
    },
    updateCompany: (updatedCompany: CompanyFormInterface) => {
      dispatch(
        updateCompanyRequest({
          companyId: companyDetail.companyId,
          companyDetail: createAndUpdateCompanyFormToApiConvertor(
            updatedCompany
          )
        })
      )
    },
    statusChangeHandlerGenerator: (
      action: keyof typeof COMPANY_STATUS_TRANSITION_TYPE
    ) => () => {
      dispatch(
        updateCompanyStatusRequest({
          companyId: companyDetail.companyId,
          action
        })
      )
    }
  }

  const companyProductHandlers = {
    createProduct: (createdProduct: ProductFormInterface) => {
      dispatch(
        createProductRequest(
          createProductFromToApiConvertor({
            ...createdProduct,
            companyId: id
          })
        )
      )
    },
    updateProduct: (productId: string) => (
      updatedProduct: ProductFormInterface
    ) => {
      dispatch(
        updateProductRequest({
          productId,
          productDetail: updateProductFromToApiConvertor(updatedProduct)
        })
      )
    },
    archiveProduct: (productId: string) => () => {
      dispatch(archiveProductRequest({ productId, companyId: id }))
    }
  }

  useEffect(() => {
    if (!newForm) {
      dispatch(getCompanyDetailRequest(id))
    }
  }, [])

  useEffect(() => {
    dispatch(
      getCompanyProductListRequest({
        page: 1,
        limit: config.product.limit,
        companyId: id,
        sortBy: 'createdTime',
        sortDirection: 'desc',
        isDeleted: false
      })
    )
  }, [])

  const [tabPanelNumber, setTabPanelNumber] = useState(0)
  const handleTabChange = (
    _event: React.ChangeEvent<any>,
    newValue: number
  ) => {
    setTabPanelNumber(newValue)
  }

  return (
    <HorizontalGridDivider>
      <SideMenu />
      <VerticalGridDivder>
        <TopBar />
        <StyledMain>
          <DetailTitleBar
            title={'Profile Detail'}
            detail={companyDetail}
            newForm={newForm}
          />
          <Paper style={{ marginTop: '10px' }}>
            <Tabs
              value={tabPanelNumber}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="standard"
            >
              <Tab label="Company" />
              {!newForm && <Tab label="Product" />}
            </Tabs>
          </Paper>
          {isFetchingCompanyDetail || isCreateOrUpdatingCompany ? (
            <Loading />
          ) : (
            <>
              <TabPanel value={tabPanelNumber} index={0}>
                <CompanyForm
                  companyDetail={companyDetail}
                  formHandlers={formHandlers}
                  disableForm={disableForm}
                  newForm={newForm}
                />
              </TabPanel>
              {!newForm && (
                <TabPanel value={tabPanelNumber} index={1}>
                  <CompanyProductTabPanel
                    productList={companyProductList}
                    companyProductHandlers={companyProductHandlers}
                  />
                </TabPanel>
              )}
            </>
          )}
        </StyledMain>
      </VerticalGridDivder>
    </HorizontalGridDivider>
  )
}

export default CompanyDetail
